/**
 * "We have this" arriving from the site (core haveit.ts). A reader of a how-long entry or a library guide said the place
 * has croissants, kept in the display case; the site's sign-up link carries it (?have=croissant&where=display).
 *   - We keep it here (localStorage, in try/catch) the moment the app loads, so nothing can lose it: not a sign-in, not the
 *     first setup, not a reload (H3). It never blocks sign-up or any safety screen.
 *   - If a person was already signed in on this browser, it goes straight into the app. If they were signed out, once they
 *     have a place the app asks one thing: "You told us you have croissants in the display case. Keep it?"
 * `seenSignedOut` is how the two are told apart: the gate marks it when it shows the sign-in with an intent waiting.
 */
import { parseHaveParams, type HaveInput } from "@logwave/core";
import { useCallback, useSyncExternalStore } from "react";

export const HAVE_KEY = "cc.have.pending";
export interface PendingHave extends HaveInput { /** The link also asked to open Ask about it afterwards. */ ask?: boolean; seenSignedOut?: boolean; at: string }

function safeStorage(): Storage | undefined {
  try { return typeof localStorage === "undefined" ? undefined : localStorage; } catch { return undefined; }
}
const subs = new Set<() => void>();
const notify = () => subs.forEach((f) => f());
let memory: PendingHave | null = null; // the same intent for this page load when storage is blocked

/** A visit from the site lands with ?have=…: keep it. Returns it, or null for a link with no known item. */
export function captureHave(search: string, storage: Storage | undefined = safeStorage()): PendingHave | null {
  const p = new URLSearchParams(search);
  const input = parseHaveParams(p);
  if (!input) return null;
  const have: PendingHave = { ...input, ...(p.get("ask") === "1" ? { ask: true } : {}), at: new Date().toISOString() };
  memory = have;
  try { storage?.setItem(HAVE_KEY, JSON.stringify(have)); } catch { /* private mode: it lives for this page load */ }
  notify();
  return have;
}
export function pendingHave(storage: Storage | undefined = safeStorage()): PendingHave | null {
  try {
    const v = JSON.parse(storage?.getItem(HAVE_KEY) ?? "null") as PendingHave | null;
    if (v && parseHaveParams(`have=${encodeURIComponent(v.key)}`)) return v;
  } catch { /* fall through to memory */ }
  return memory;
}
export function clearHave(storage: Storage | undefined = safeStorage()): void {
  memory = null;
  try { storage?.removeItem(HAVE_KEY); } catch { /* ignore */ }
  notify();
}
/** The sign-in screen is showing and an intent is waiting: from here it is the "Keep it?" path, not the straight-in one. */
export function markSignedOut(storage: Storage | undefined = safeStorage()): void {
  const v = pendingHave(storage);
  if (!v || v.seenSignedOut) return;
  const next = { ...v, seenSignedOut: true };
  memory = next;
  try { storage?.setItem(HAVE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  notify();
}
/** The link's query without the intent, so a reload never starts it again. */
export function withoutHaveParams(search: string): string {
  const p = new URLSearchParams(search);
  for (const k of ["have", "where", "amt", "on", "ask"]) p.delete(k);
  const s = p.toString();
  return s ? `?${s}` : "";
}

/** The waiting intent, live: re-renders when it is captured, marked or cleared. */
const snapshot = (): string => { try { return localStorage.getItem(HAVE_KEY) ?? JSON.stringify(memory); } catch { return JSON.stringify(memory); } };
export function usePendingHave(): { pending: PendingHave | null; clear: () => void } {
  useSyncExternalStore((f) => { subs.add(f); return () => subs.delete(f); }, snapshot, () => "null");
  const clear = useCallback(() => clearHave(), []);
  return { pending: pendingHave(), clear };
}
